import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {CalendarIcon, ClockRewindIcon} from '@/components/ui/icon/Icon'
import {
    StyledEllipsisText,
    StyledEmptyState,
    StyledEventRow,
    StyledEventTime,
    StyledEventsList,
    StyledHomeCard,
    StyledHomeCardContent
} from './style'
import {IconContainer} from '@/components/ui/icon-container/IconContainer'
import {HomeEventsSkeleton} from './HomeEventsSkeleton'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Badge} from '@/components/ui/badge/Badge'
import {routes} from '@/utilities/constants/routes'
import {generatePath} from 'react-router-dom'
import dayjs from 'dayjs'
import {formatDate, formatLocaleDate} from '@/dayjs'
import {useAuthStore} from '@/features/auth/store/store'
import {useGetHomePageData} from '../../query/useGetHomePageData'
import {eventTypeMapToLabel} from '@/features/agenda/utils'
import {EventSmall} from '@/features/agenda/types'
import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'

const getEventStart = (event: EventSmall) =>
    event.time ? dayjs(`${formatDate(event.date, 'YYYY-MM-DD')} ${event.time}`) : null

const formatEventTimeRange = (event: EventSmall) => {
    const start = getEventStart(event)
    if (!start) return '-'
    const end = start.add(event.duration ?? 0, 'minutes')
    return `${formatLocaleDate(start.toDate(), 'HH:mm')} - ${formatLocaleDate(end.toDate(), 'HH:mm')}`
}

const formatEventPatients = (event: EventSmall) => {
    const names = event.eventPatients?.map(({patient}) => patient?.fullName).filter(Boolean) ?? []
    if (names.length === 0) return '-'
    return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]
}

export const HomeEventsCard = () => {
    const {t} = useTranslation()
    const user = useAuthStore(state => state.user)

    const {data, isLoading} = useGetHomePageData({fields: ['events']})

    const today = dayjs()
    const formattedDate = formatLocaleDate(today.toDate(), 'DD-MM-YYYY')

    // The backend returns today's events only, unordered: sort them by start time (events without a time last)
    const events = useMemo(
        () =>
            [...(data?.events ?? [])].sort((a, b) => {
                const aStart = getEventStart(a)
                const bStart = getEventStart(b)
                if (!aStart || !bStart) return Number(!aStart) - Number(!bStart)
                return aStart.valueOf() - bStart.valueOf()
            }),
        [data?.events]
    )

    const searchParams = new URLSearchParams({
        category: 'custom',
        subjectIds: user?.id ?? ''
    }).toString()

    return (
        <StyledHomeCard>
            <StyledHomeCardContent>
                <Flexbox align="center" justify="space-between" gap={4} fullWidth>
                    <Flexbox align="center" gap={4}>
                        <IconContainer size="lg" variant="primary">
                            <CalendarIcon />
                        </IconContainer>
                        <Flexbox direction="column" gap={0.5}>
                            <Text size="textMd" weight={500} palette="neutral" shade="900">
                                {t('agenda:scheduled_events_count')}
                            </Text>
                            <Text size="textSm" palette="neutral" shade="600">
                                {t('homepage:today_date', {date: formatLocaleDate(today.toDate(), 'dddd D MMMM')})}
                            </Text>
                        </Flexbox>
                    </Flexbox>
                    {!isLoading && events.length > 0 && (
                        <Badge variant="primary" size="sm">
                            {events.length}
                        </Badge>
                    )}
                </Flexbox>

                {isLoading ? (
                    <HomeEventsSkeleton />
                ) : events.length === 0 ? (
                    <StyledEmptyState>
                        <CalendarIcon size={18} />
                        <Text size="textSm" palette="neutral" shade="500">
                            {t('homepage:no_events')}
                        </Text>
                    </StyledEmptyState>
                ) : (
                    <StyledEventsList>
                        {events.map(event => {
                            const accessType = event.eventConsultants?.[0]?.accessType?.accessType
                            return (
                                <StyledEventRow key={event.id}>
                                    <StyledEventTime>
                                        <ClockRewindIcon size={14} />
                                        <Text size="textSm" weight={500} palette="neutral" shade="900">
                                            {formatEventTimeRange(event)}
                                        </Text>
                                    </StyledEventTime>
                                    <StyledEllipsisText
                                        size="textSm"
                                        weight={500}
                                        palette="neutral"
                                        shade="900"
                                        title={formatEventPatients(event)}
                                    >
                                        {formatEventPatients(event)}
                                    </StyledEllipsisText>
                                    <Text size="textSm" palette="neutral" shade="600">
                                        {accessType ? t(eventTypeMapToLabel[accessType]) : ''}
                                    </Text>
                                </StyledEventRow>
                            )
                        })}
                    </StyledEventsList>
                )}
            </StyledHomeCardContent>

            {events.length > 0 && (
                <ButtonLink
                    variant="secondary"
                    to={{
                        pathname: generatePath(routes.AGENDA.path, {
                            entity: 'users',
                            view: 'agenda',
                            date: formattedDate
                        }),
                        search: searchParams
                    }}
                >
                    {t('homepage:see_all_events')}
                </ButtonLink>
            )}
        </StyledHomeCard>
    )
}
